import React from 'react';
import { OfferRule } from '@/hooks/useOfferRules';
import { availableFields } from '@/utils/ruleDefinitions';
import { formatCondition, combinatorHeading, combinatorJoiner } from '@/utils/ruleFormat';
import { ArrowDown, Play, Flag, Gift } from 'lucide-react';
import type { Tables } from '@/integrations/supabase/types';

interface RuleFlowVisualizerProps {
  rule: OfferRule;
  offerTemplates?: Tables<'offer_templates'>[];
}

// A plain-language, top-to-bottom read of the rule. It mirrors the real
// combinators (ALL vs ANY, per group and between groups) instead of asserting
// "all must be true", so the preview never lies about the logic. Fully fluid:
// the flow is a single centered column that reads at 360px and up.
const RuleFlowVisualizer: React.FC<RuleFlowVisualizerProps> = ({ rule, offerTemplates = [] }) => {
  const groups = rule.condition_groups ?? [];
  const actions = rule.actions ?? [];

  const offerName = (templateId?: string) =>
    offerTemplates.find((t) => t.offer_id === templateId)?.offer_name;

  return (
    <div className="rounded-lg border bg-muted/40 p-4 h-full overflow-auto">
      <div className="mx-auto flex w-full max-w-md flex-col items-center">
        {/* Start */}
        <div className="inline-flex items-center gap-2 rounded-full border border-status-info/30 bg-status-info/10 px-3 py-1.5">
          <Play className="h-3.5 w-3.5 text-status-info" />
          <span className="text-sm font-semibold text-status-info">Order or customer arrives</span>
        </div>

        {groups.length === 0 && (
          <>
            <ArrowDown className="my-2 h-4 w-4 text-muted-foreground" />
            <p className="rounded-md border border-dashed border-border bg-card px-4 py-3 text-center text-sm text-muted-foreground">
              No conditions yet. This rule would match every order.
            </p>
          </>
        )}

        {groups.map((group, index) => (
          <React.Fragment key={group.id || index}>
            <ArrowDown className="my-2 h-4 w-4 text-muted-foreground" />
            {/* Between-group combinator: show how this group joins the previous one. */}
            {index > 0 && (
              <span className="mb-2 rounded-full bg-muted px-3 py-0.5 text-xs font-bold uppercase tracking-wide text-muted-foreground">
                {combinatorJoiner(rule.group_combinator)}
              </span>
            )}
            <div className="w-full rounded-lg border border-border bg-card p-4 shadow-sm">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-brand-gray">
                Group {index + 1} · {combinatorHeading(group.combinator)}
              </p>
              {group.conditions.length > 0 ? (
                <ul className="space-y-1.5">
                  {group.conditions.map((condition, condIndex) => (
                    <li
                      key={condition.id || condIndex}
                      className="flex items-start gap-2 text-sm text-foreground"
                    >
                      <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-status-info" />
                      <span>{formatCondition(condition, availableFields)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">No conditions in this group.</p>
              )}
            </div>
          </React.Fragment>
        ))}

        <ArrowDown className="my-2 h-4 w-4 text-muted-foreground" />

        {/* Actions */}
        <div className="w-full rounded-lg border border-status-success/30 bg-status-success/10 p-4">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-brand-green">
            <Gift className="h-3.5 w-3.5" />
            Then
          </p>
          {actions.length > 0 ? (
            <ul className="space-y-1.5">
              {actions.map((action, actionIndex) => (
                <li key={action.id || actionIndex} className="flex items-start gap-2 text-sm text-foreground">
                  <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-status-success" />
                  <span>
                    Assign offer
                    {offerName(action.value?.offer_template_id) ? (
                      <>: <strong>{offerName(action.value?.offer_template_id)}</strong></>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No actions yet.</p>
          )}
        </div>

        <ArrowDown className="my-2 h-4 w-4 text-muted-foreground" />
        <div className="inline-flex items-center gap-2 rounded-full border border-status-info/30 bg-status-info/10 px-3 py-1.5">
          <Flag className="h-3.5 w-3.5 text-status-info" />
          <span className="text-sm font-semibold text-status-info">Done</span>
        </div>
      </div>
    </div>
  );
};

export default RuleFlowVisualizer;
